import type { MentionRow } from './generator';
import { rowId } from './sentimentOverrides';

/**
 * When a mention was posted, and how a table writes it.
 *
 * One module for both halves, because they are one decision: a table cell reads
 * `Mon Sep 14, 9:42 PM` only if the timestamp behind it carries a minute, and
 * the corpus stops at the hour.
 */

const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;
const MINUTES_PER_HOUR = 60;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * The exact moment a mention was posted.
 *
 * The corpus records a local-midnight `day` and an integer `hour` — see
 * `MentionRow` — so every row of it lands precisely on the hour, and a column of
 * timestamps read straight off it would print `9:00 PM` down the whole table.
 *
 * The minute is therefore derived from the row's own id: a fixed, arbitrary
 * offset inside the hour the corpus *did* record, stable for the life of a row
 * so two tables showing the same post agree, and so a sort by time is a total
 * order rather than a pile of ties broken by whatever order the rows arrived in.
 * It is fixture precision, in the same spirit as `pageConversations`' invented
 * post copy: the hour is data, the minute is presentation.
 *
 * A prime stride so consecutive rows do not walk the clock in step.
 */
export function postedAt(row: MentionRow): number {
  return row.day + row.hour * HOUR_MS + ((rowId(row) * 37) % MINUTES_PER_HOUR) * MINUTE_MS;
}

/**
 * `Mon Sep 14, 9:42 PM` — how every table writes a **specific moment**, as
 * against a date *range*, which stays `Sep 1 – Sep 30` wherever one is shown.
 * Settled with the user 2026-09-23.
 *
 * Composed by hand rather than through `Intl.DateTimeFormat`, which puts a comma
 * after the weekday (`Mon, Sep 14`) and offers no way to drop it — the one comma
 * in this format separates the date from the time.
 *
 * The year is left off deliberately. Every window the app can show sits inside
 * one year, and the weekday is the part a reader actually uses: "was this a
 * Sunday?" is a question about a spike, and "was this 2026?" is not.
 */
export function postedLabel(epoch: number): string {
  const d = new Date(epoch);

  const hour24 = d.getHours();
  /* Midnight and noon are 12, not 0 — `hour % 12` alone prints `0:42 AM`. */
  const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const minute = String(d.getMinutes()).padStart(2, '0');
  const period = hour24 < 12 ? 'AM' : 'PM';

  return `${WEEKDAYS[d.getDay()]} ${MONTHS[d.getMonth()]} ${d.getDate()}, ${hour}:${minute} ${period}`;
}
